const getEnvValue = (key) => {
  if (typeof import.meta !== 'undefined' && import.meta.env && key in import.meta.env) {
    return import.meta.env[key]
  }

  if (typeof process !== 'undefined' && process.env && key in process.env) {
    return process.env[key]
  }

  return ''
}

const DEFAULT_BASE_URL = 'https://api.thoughtly.com'

const normalizeBaseUrl = (url) => url.replace(/\/+$/, '')

const withoutInterviewPath = (url) => normalizeBaseUrl(url).replace(/\/interview$/i, '')

const withInterviewPath = (url) => {
  const normalized = normalizeBaseUrl(url)

  if (/\/interview$/i.test(normalized)) {
    return normalized
  }

  return `${normalized}/interview`
}

const getConfig = (overrides = {}) => {
  const apiKey = (overrides.apiKey ?? getEnvValue('VITE_THOUGHTLY_API_KEY') ?? '').trim()
  const teamId = (overrides.teamId ?? getEnvValue('VITE_THOUGHTLY_TEAM_ID') ?? '').trim()
  const providedBaseUrl = (overrides.baseUrl ?? getEnvValue('VITE_THOUGHTLY_API_URL') ?? '').trim()
  const baseUrl = withoutInterviewPath(providedBaseUrl || DEFAULT_BASE_URL) || DEFAULT_BASE_URL
  const interviewBaseUrl = withInterviewPath(baseUrl)

  return { apiKey, teamId, baseUrl, interviewBaseUrl }
}

export const isThoughtlyConfigured = (overrides = {}) => {
  const { apiKey, teamId } = getConfig(overrides)
  return Boolean(apiKey && teamId)
}

const buildHeaders = (config, { includeJson = false } = {}) => {
  const headers = {
    'x-api-token': config.apiKey,
    team_id: config.teamId
  }

  if (includeJson) {
    headers['Content-Type'] = 'application/json'
  }

  return headers
}

const handleThoughtlyResponse = async (response, requestContext = {}) => {
  const rawBody = await response.text()
  const responseHeaders = Object.fromEntries(response.headers.entries())
  const contentType = responseHeaders['content-type'] || response.headers.get('content-type') || ''

  console.debug('[Thoughtly] Request diagnostics', {
    url: requestContext.url,
    headers: requestContext.headers,
    method: requestContext.method,
    status: response.status,
    responseHeaders,
    bodyPreview: rawBody.slice(0, 500)
  })

  const isSuccessfulStatus = response.ok
  const hasBody = rawBody.length > 0
  const isJson = contentType.toLowerCase().includes('json')

  if (!isSuccessfulStatus || (hasBody && !isJson)) {
    const error = new Error(
      `Thoughtly API request failed (status ${response.status}). Headers: ${JSON.stringify(responseHeaders)}. Body: ${rawBody}`
    )
    error.status = response.status
    error.headers = responseHeaders
    error.body = rawBody
    throw error
  }

  let data = null

  if (rawBody) {
    try {
      data = JSON.parse(rawBody)
    } catch (parseError) {
      const error = new Error('Failed to parse Thoughtly response as JSON')
      error.status = response.status
      error.headers = responseHeaders
      error.body = rawBody
      error.cause = parseError
      throw error
    }
  }

  if (data?.error) {
    const error = new Error(data.error)
    error.status = response.status
    error.details = data
    throw error
  }

  return data
}

export const initiateThoughtlyCall = async ({
  contact,
  agent,
  customVariables = {},
  callerId,
  overrides = {}
}) => {
  const config = getConfig(overrides)

  if (!isThoughtlyConfigured(config)) {
    throw new Error('Thoughtly API is not configured')
  }

  if (!contact?.phone) {
    throw new Error('A valid contact phone number is required to initiate a call')
  }

  const headers = buildHeaders(config, { includeJson: true })
  const metadata = {
    contactId: contact.id,
    recruitmentStage: contact.recruitmentStage,
    agentName: agent?.name,
    ...customVariables
  }

  const filteredMetadata = Object.fromEntries(
    Object.entries(metadata).filter(([, value]) => {
      if (value === null || value === undefined) {
        return false
      }

      if (typeof value === 'string' && value.trim() === '') {
        return false
      }

      return true
    })
  )

  const contactName =
    [contact.firstName, contact.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    contact.name ||
    contact.email ||
    contact.phone ||
    'Contact'

  const contactPayload = {
    phone_number: contact.phone,
    name: contactName,
    email: contact.email || ''
  }

  const createContactUrl = `${config.baseUrl}/contact/create`
  const createContactOptions = {
    method: 'POST',
    headers,
    body: JSON.stringify(contactPayload)
  }

  console.debug('[Thoughtly] Preparing request', {
    phase: 'beforeFetch',
    method: createContactOptions.method,
    url: createContactUrl,
    headers,
    payload: contactPayload
  })

  const createContactResponse = await fetch(createContactUrl, createContactOptions)
  const contactResponse = await handleThoughtlyResponse(createContactResponse, {
    url: createContactUrl,
    headers,
    method: createContactOptions.method
  })

  console.log('[Thoughtly] Create contact response:', JSON.stringify(contactResponse, null, 2))

  const contactIdentifier =
    contactResponse?.data?.id ||
    contactResponse?.contact_id ||
    contactResponse?.contactId ||
    contactResponse?.contact?.id ||
    contactResponse?.contact?.identifier ||
    contactResponse?.data?.contact?.id ||
    contactResponse?.data?.contact?.identifier ||
    contactResponse?.id ||
    contactResponse?.identifier

  if (!contactIdentifier) {
    const error = new Error('Failed to retrieve contact identifier from Thoughtly response')
    error.details = contactResponse
    throw error
  }

  const callPayload = {
    contact_id: contactIdentifier,
    interview_id: agent?.thoughtlyAgentId || agent?.id,
    metadata: filteredMetadata
  }

  if (callerId) {
    callPayload.caller_id = callerId
  }

  const initiateCallUrl = `${config.baseUrl}/contact/call`
  const initiateCallOptions = {
    method: 'POST',
    headers,
    body: JSON.stringify(callPayload)
  }

  console.debug('[Thoughtly] Preparing request', {
    phase: 'beforeFetch',
    method: initiateCallOptions.method,
    url: initiateCallUrl,
    headers,
    payload: callPayload
  })

  const initiateCallResponse = await fetch(initiateCallUrl, initiateCallOptions)

  return handleThoughtlyResponse(initiateCallResponse, {
    url: initiateCallUrl,
    headers,
    method: initiateCallOptions.method
  })
}

export const fetchThoughtlyAgents = async (overrides = {}) => {
  const config = getConfig(overrides)

  if (!isThoughtlyConfigured(config)) {
    throw new Error('Thoughtly API is not configured')
  }

  const url = config.interviewBaseUrl
  const headers = buildHeaders(config)
  const options = {
    method: 'GET',
    headers
  }

  console.debug('[Thoughtly] Preparing request', { phase: 'beforeFetch', method: options.method, url })
  const response = await fetch(url, options)

  const data = await handleThoughtlyResponse(response, { url, headers, method: options.method })

  const possibleCollections = [
    data?.data?.interviews,
    data?.data?.agents,
    data?.data,
    data?.interviews,
    data?.agents,
    data
  ]

  for (const collection of possibleCollections) {
    if (Array.isArray(collection)) {
      return collection
    }
  }

  return []
}

export const fetchThoughtlyCallHistory = async (contactIdentifier, overrides = {}) => {
  const config = getConfig(overrides)

  if (!isThoughtlyConfigured(config)) {
    throw new Error('Thoughtly API is not configured')
  }

  if (!contactIdentifier) {
    throw new Error('A contact identifier is required to fetch Thoughtly call history')
  }

  const url = `${config.baseUrl}/teams/${config.teamId}/contacts/${contactIdentifier}/calls`
  const headers = buildHeaders(config)
  const options = {
    method: 'GET',
    headers
  }

  console.debug('[Thoughtly] Preparing request', { phase: 'beforeFetch', method: options.method, url })
  const response = await fetch(url, options)

  const data = await handleThoughtlyResponse(response, { url, headers, method: options.method })

  if (Array.isArray(data)) {
    return data
  }

  return data?.calls || []
}

export const fetchThoughtlyInterviewResponses = async (interviewId, overrides = {}) => {
  const config = getConfig(overrides)

  if (!isThoughtlyConfigured(config)) {
    throw new Error('Thoughtly API is not configured')
  }

  if (!interviewId) {
    throw new Error('An interview ID is required to fetch Thoughtly responses')
  }

  const url = `${config.interviewBaseUrl}/${interviewId}/responses`
  const headers = buildHeaders(config)
  const options = {
    method: 'GET',
    headers
  }

  console.debug('[Thoughtly] Preparing request', { phase: 'beforeFetch', method: options.method, url })
  const response = await fetch(url, options)

  const apiResponse = await handleThoughtlyResponse(response, { url, headers, method: options.method })

  try {
    console.log('[Thoughtly API] Full response:', JSON.stringify(apiResponse, null, 2))
  } catch (error) {
    console.log('[Thoughtly API] Full response could not be stringified', error)
  }

  console.log('[Thoughtly API] Response data array:', apiResponse?.data)

  const possibleCollections = [
    apiResponse?.data?.responses,
    apiResponse?.data,
    apiResponse?.responses,
    apiResponse
  ]

  for (const collection of possibleCollections) {
    if (Array.isArray(collection)) {
      collection.forEach((callRecord) => {
        try {
          console.log('[Thoughtly API] Single call object:', JSON.stringify(callRecord, null, 2))
        } catch (error) {
          console.log('[Thoughtly API] Single call object could not be stringified', error)
        }

        if (callRecord && typeof callRecord === 'object') {
          console.log('[Thoughtly API] Available fields:', Object.keys(callRecord))
        } else {
          console.log('[Thoughtly API] Available fields:', [])
        }
      })
      return collection
    }
  }

  return []
}

export default {
  isThoughtlyConfigured,
  initiateThoughtlyCall,
  fetchThoughtlyAgents,
  fetchThoughtlyCallHistory,
  fetchThoughtlyInterviewResponses
}
